/**
 * Long-form showcase copy for a single listing.
 *
 * The standard listing page is a product page: price, files, buy. This is the
 * argument for one skill — the problem it exists for, what it actually does,
 * and what it refuses to do. It is hand-written per skill on purpose, because
 * the parts worth reading (the failure it avoids, the honest limits) cannot be
 * generated from a manifest.
 *
 * Live numbers — price, downloads, file list, scan status — are NOT stored
 * here. The page reads those from the listing API at render time so this file
 * can never quietly go stale against the marketplace.
 */

/**
 * Bump when a showcase asset is replaced in place.
 *
 * Files under public/ keep their filename across a rebuild, and Cloudflare
 * fronts the site with a four-hour TTL, so a re-uploaded demo keeps serving the
 * old bytes from the edge long after the deploy looks finished. Hashed bundle
 * filenames make this a non-issue for JS and CSS; static media has to say so
 * itself.
 */
const ASSET_V = 2;
const v = (path) => `${path}?v=${ASSET_V}`;

export const SHOWCASES = {
  'harness-shape-judge-an-agent-framework-by-its-imports': {
    name: 'harness-shape',
    tagline: 'Judge an agent framework by its imports, not its feature list.',
    banner: v('/showcase/harness-shape/banner.png'),
    bannerAlt:
      'The import graph of openai-agents-python: 50 modules, 329 imports, with '
      + 'run_context at the centre because 22 of them import it.',
    demo: {
      src: v('/showcase/harness-shape/demo.mp4'),
      poster: v('/showcase/harness-shape/demo-poster.png'),
      // Window title bar. Short enough not to truncate at mobile widths — the
      // full command is already printed in the footage.
      label: 'shape.py — openai-agents-python',
      aspect: '1280 / 860',
      caption:
        'A real run against openai/openai-agents-python. Nothing is staged — ' +
        'the module names and counts on screen are what the tool printed.',
    },

    lede:
      'Every agent framework ships the same feature list. Memory, tracing, ' +
      'guardrails, handoffs. So the list tells you nothing, and you end up ' +
      'choosing on vibes. Imports are harder to fake: they are what had to be ' +
      'true for the code to run.',

    headings: {
      install: 'Three steps, no dependencies',
      checksEyebrow: 'What it reads',
      checks: 'Three structural checks',
      proofEyebrow: 'What it found',
    },
    pill: 'Standard library only',
    run: 'python3 scripts/shape.py https://github.com/openai/openai-agents-python',

    install: {
      steps: [
        'Download the package from this page.',
        'Unzip it into .claude/skills/ in your project.',
        'Ask Claude to use it, or run the script directly.',
      ],
      invoke: 'Use harness-shape to check this agent framework before I commit to it',
      cli: 'python3 scripts/shape.py ./some/local/checkout --json',
      note:
        'No pip install. An AST walk of the package finds no third-party ' +
        'import — it runs on the Python standard library alone.',
    },

    checks: [
      {
        n: '01',
        title: 'The context object, and what it imports back',
        body:
          'Find the module holding run state, then read its outgoing imports. ' +
          'Importing shared types and error classes is ordinary. Importing the ' +
          'loop or the tool layer is the coupling you feel later, because then ' +
          'the context cannot be tested or reused without dragging the ' +
          'machinery along.',
        kicker: 'This check deliberately refuses to grade.',
      },
      {
        n: '02',
        title: 'Whether the tool layer imports the tracer',
        body:
          'Not "is there tracing", which is always yes. Tracing that wraps only ' +
          'the outer loop gives you a trace of the loop and nothing about what ' +
          'happened inside a tool call — which is exactly where you need it at ' +
          'two in the morning.',
        kicker: 'The count matters less than who is doing the importing.',
      },
      {
        n: '03',
        title: 'Whether anything on the hot path imports the guardrails',
        body:
          'A guard module nothing calls is available, not enforced. If the only ' +
          'thing between your agent and a destructive action is a paragraph in ' +
          'the system prompt, that is a suggestion.',
        kicker: 'You can see which a framework chose from the import edges.',
      },
    ],

    proof: {
      repo: 'openai/openai-agents-python',
      scale: '50 modules, 329 imports',
      rows: [
        ['Context object', 'run_context', 'read by 22 of 50, imports 6 back'],
        ['Observability', 'tracing', 'read by 15 of 50 — and tool is one of them'],
        ['Guardrails', 'guardrail, tool_guardrails', 'imported by the run loop and the tool layer'],
      ],
      note:
        'Findings from a live run at build time. The tool names every module ' +
        'behind every finding so you can go and read the code yourself.',
    },

    trap: {
      title: 'The failure it was built to avoid',
      body:
        'Cap the module list at the 22 largest and this repo reports no ' +
        'guardrails at all, because a three-hundred-line guard module falls ' +
        'below the cut. At full scope it is there and it is enforced. Same ' +
        'repo, opposite answer, from an arbitrary size cut. The same trap makes ' +
        'the context object look like it imports nothing back, when it imports ' +
        'six. So it counts everything by default.',
    },

    limits: [
      'It counts and names. It never scores, ranks, or tells you which framework to pick.',
      'Modules are matched by name, so a framework that spells things unusually can be missed. A wrong guess is visible rather than hidden, because every finding prints the module names it used.',
      'Python, JavaScript, TypeScript and Go. TypeScript path aliases resolve from tsconfig.json.',
      'Plenty of good code breaks all three checks. They are the questions worth asking first, not a rubric.',
    ],

    closing:
      'Three checks, ten passing tests, and no opinion it has not shown you the ' +
      'evidence for.',
  },

  'temporal-reasoning-sleuth': {
    name: 'Temporal Reasoning Sleuth',
    tagline: 'Ask what led to this, not what happened that month.',
    banner: v('/showcase/temporal-reasoning-sleuth/banner.png'),
    bannerAlt:
      'A causal chain of four events on a rail — breach, decision, rollout, '
      + 'mobile update — with two unrelated events on a lower rail.',
    demo: {
      src: v('/showcase/temporal-reasoning-sleuth/demo.mp4'),
      poster: v('/showcase/temporal-reasoning-sleuth/demo-poster.png'),
      label: 'loreto-temporal — trace',
      aspect: '1280 / 1200',
      caption:
        'A real run against the demo store the CLI ships with: six events, four ' +
        'causal edges. The chain on screen is what trace returned, not an ' +
        'illustration of one.',
    },

    lede:
      'Give a model a year of decisions and it gets a timeline dump: everything ' +
      'that happened, in order, none of it connected. The question you actually ' +
      'asked was why the system ended up like this — and that answer is a chain ' +
      'through the timeline, not the timeline.',

    headings: {
      // Not 'no dependencies' — this one installs an npm package with a
      // native SQLite module. The harness-shape wording would be a false claim.
      install: 'Three steps, local-first',
      checksEyebrow: 'What it answers',
      checks: 'Three temporal queries',
      proofEyebrow: 'What it returned',
    },
    pill: 'Local SQLite · CLI + MCP',
    run: 'loreto-temporal trace --event evt_mobile_update',

    install: {
      steps: [
        'Install the npm package — it carries the CLI and the MCP server.',
        'Spin up the local SQLite store, then seed it or import your own events.',
        'Ask Claude through the /temporal slash command, or wire it in over MCP.',
      ],
      invoke: '/temporal what decisions led to our current auth setup?',
      cli: 'npx @loreto-labs/skill-temporal install\nloreto-temporal init && loreto-temporal seed',
      note:
        'The download on this page is the skill itself — SKILL.md, README and a ' +
        'reference. The engine is a separate published package, so the CLI and ' +
        'MCP server come from npm rather than the zip.',
    },

    checks: [
      {
        n: '01',
        title: 'Sequence — what happened, in order',
        body:
          'Events for one entity inside a window. This is the part every tool ' +
          'gives you, and on its own it is the thing that buries a model: a ' +
          'wall of changes with no indication which of them mattered.',
        kicker: 'sequence --entity auth-service --from-date … --to-date …',
      },
      {
        n: '02',
        title: 'Trace — what led to an event',
        body:
          'Walk the causal edges backwards from an event, four hops by default, ' +
          'and get the ancestors in topological order — each step with its date, ' +
          'actors, what changed and why. It comes back as a structured causal ' +
          'chain, shaped to drop straight into a prompt.',
        kicker: 'The curated slice, instead of the whole timeline.',
      },
      {
        n: '03',
        title: 'Downstream — what an event caused',
        body:
          'The same walk forwards: everything a decision or incident set in ' +
          'motion. That is the base you need before you can ask the ' +
          'counterfactual — what would have followed if this had gone the other ' +
          'way.',
        kicker: 'downstream --event evt_auth_breach',
      },
    ],

    proof: {
      repo: 'trace --event evt_mobile_update',
      scale: '6 events, 4 causal edges in the demo store',
      rows: [
        ['Step 1', 'evt_auth_breach', 'Credential-stuffing breach exposed weak session handling'],
        ['Step 2', 'dec_oauth2', 'ADR approved to adopt OAuth2 — direct response to the breach'],
        ['Step 3', 'evt_oauth2_impl', 'Rolled out OAuth2 across core services'],
      ],
      note:
        'The chain a live run returned, ending at the mobile auth update. Two ' +
        'older events in the same store are left out because nothing links them ' +
        'to this one — which is the entire point.',
    },

    trap: {
      title: 'Why a timeline is the wrong shape',
      body:
        'Context windows make the obvious approach feel workable: paste in every ' +
        'event and let the model sort it out. It does not sort it out. It ' +
        'weights recent entries, invents connections between things that merely ' +
        'happened near each other, and loses the thread across months. Causality ' +
        'is an edge you either recorded or did not — so this stores the edge, ' +
        'and retrieves along it. Distant history comes back summarised rather ' +
        'than dumped: full detail under thirty days, nodes to a hundred and ' +
        'eighty, a summary beyond that.',
    },

    limits: [
      'It is the read path. Capturing and ingesting events is a separate concern, and the skill says so rather than pretending to do both.',
      'Causal edges are asserted by whoever writes them, not inferred. The tool will faithfully trace a chain someone recorded wrongly.',
      'The store is a local SQLite file. That is the point — no graph database to run — but it also means you own where it lives and how it gets backed up.',
      'Four edge types: CAUSED, TRIGGERED, PRECEDED, SUPERSEDED_BY. Anything subtler than those has to be squeezed into one of them.',
    ],

    closing:
      'A local SQLite file, fourteen MCP tools, and a passing test — so an agent ' +
      'can answer why instead of listing when.',
  },
};

export function getShowcase(slug) {
  return SHOWCASES[slug] || null;
}
